/**
 * The device credential's wire form, in one place (technical-design §6.1, §14.1;
 * decision-log D83).
 *
 * A device token is **not** a JWT and **not** a user credential, so it does not travel under
 * `Bearer`. Keeping the two schemes apart on the wire is the point: no endpoint can accept a
 * user where it wanted a stick, or the reverse. The API's `_parse_device_token`
 * (`api/app/routers/device/auth.py`) accepts `Device <token>` and refuses everything else, so a
 * `Bearer` device token is not a weaker credential — it is no credential at all, and every
 * manifest fetch, event batch and heartbeat 401s before a body is ever parsed.
 *
 * This module exists so there is exactly one place to be wrong. It is asserted against the
 * committed vector `api/tests/routes_device/vectors/device-auth.json` from both sides:
 * `player/test/device-auth-contract.test.ts` drives the real HTTP client and reads the header it
 * actually sends, and `api/tests/routes_device/test_player_auth_matches_this_scheme.py` parses
 * this file off disk and feeds what it finds to the server's own parser. Neither suite can be
 * green alone.
 */

/** The `Authorization` scheme for a device token. Never `Bearer` — that is the user JWT. */
export const DEVICE_AUTH_SCHEME = 'Device';

/** The header the scheme travels in. */
export const DEVICE_AUTH_HEADER = 'Authorization';

/** Renders the one header value every authenticated device request carries. */
export function deviceAuthorizationHeader(token: string): string {
  return `${DEVICE_AUTH_SCHEME} ${token}`;
}
